import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import {
  getOnlineClassImageUrl,
  onlineClassApi,
} from "../../api/onlineClassApi";
import { buildAppPath, isAdminRole, isProviderRole } from "../../utils/auth";
import "../../components/MasterCrud.css";

function statusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "live") return "LIVE";
  if (s === "scheduled") return "Scheduled";
  if (s === "ended") return "Ended";
  if (s === "cancelled") return "Cancelled";
  return status || "—";
}

export default function OnlineClassesList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const canManage = isAdminRole() || isProviderRole();

  const showToast = (message, severity = "info") => {
    setToast({ open: true, message, severity });
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await onlineClassApi.list({
        page: 1,
        limit: 100,
        search: search.trim() || undefined,
        status: status || undefined,
      });
      setRows(data.items || []);
    } catch (err) {
      showToast(err.message || "Failed to load classes", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAction = async (id, action) => {
    setBusyId(id);
    try {
      if (action === "start") {
        await onlineClassApi.start(id);
        showToast("Class started", "success");
      }
      if (action === "end") {
        if (!window.confirm("Are you sure you want to end this live class?")) {
          return;
        }
        await onlineClassApi.end(id);
        showToast("Class ended", "success");
      }
      if (action === "cancel") {
        if (!window.confirm("Cancel this class?")) return;
        await onlineClassApi.cancel(id);
        showToast("Class cancelled", "success");
      }
      await load();
    } catch (err) {
      showToast(err.message || "Action failed", "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="master-page">
      <div className="master-card">
        <div className="master-card__header">
          <h2>Online Classes</h2>
          <p>Manage live astrology classes, start/end sessions, and view attendance.</p>
        </div>

        <div className="master-form" style={{ marginTop: 12 }}>
          <div className="master-form__grid">
            <div className="master-form__field">
              <label htmlFor="oc-search">Search</label>
              <input
                id="oc-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search classes..."
              />
            </div>
            <div className="master-form__field">
              <label htmlFor="oc-status">Status</label>
              <select
                id="oc-status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="live">LIVE</option>
                <option value="upcoming">Upcoming</option>
                <option value="ended">Ended</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <div className="master-form__actions" style={{ marginTop: 14 }}>
            <button type="button" className="master-btn master-btn--primary" onClick={load}>
              Search
            </button>
            {canManage ? (
              <Link
                to={buildAppPath("create-online-class")}
                className="master-btn master-btn--primary"
                style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}
              >
                + Create Class
              </Link>
            ) : null}
          </div>
        </div>
      </div>

      <div className="master-card">
        <div className="master-card__header">
          <h3>Saved Online Classes</h3>
          <p>
            {loading
              ? "Loading..."
              : `${rows.length} Online Class${rows.length === 1 ? "" : "es"}`}
          </p>
        </div>

        <div className="master-table-wrap">
          <table className="master-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Thumbnail</th>
                <th>Title</th>
                <th>Astrologer</th>
                <th>Schedule</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="master-table__empty">
                    No online classes found. Create one from Create Online Class.
                  </td>
                </tr>
              ) : null}
              {rows.map((row, index) => (
                <tr key={row.id}>
                  <td>{index + 1}</td>
                  <td>
                    {row.thumbnail ? (
                      <img
                        src={getOnlineClassImageUrl(row.thumbnail)}
                        alt=""
                        style={{
                          width: 48,
                          height: 48,
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{row.title}</td>
                  <td>{row.astrologer?.name || "—"}</td>
                  <td>
                    {row.scheduledStart
                      ? new Date(row.scheduledStart).toLocaleString()
                      : "—"}
                  </td>
                  <td>{statusLabel(row.status)}</td>
                  <td>
                    <div className="master-table__actions">
                      <button
                        type="button"
                        className="master-btn master-btn--edit"
                        onClick={() =>
                          navigate(buildAppPath(`online-classes/${row.id}`))
                        }
                      >
                        View
                      </button>
                      {canManage && row.status === "scheduled" ? (
                        <button
                          type="button"
                          className="master-btn master-btn--edit"
                          onClick={() =>
                            navigate(
                              buildAppPath(`online-classes/${row.id}/edit`)
                            )
                          }
                        >
                          Edit
                        </button>
                      ) : null}
                      {canManage && row.status === "scheduled" ? (
                        <button
                          type="button"
                          className="master-btn master-btn--primary"
                          disabled={busyId === row.id}
                          onClick={() => runAction(row.id, "start")}
                        >
                          Start
                        </button>
                      ) : null}
                      {canManage && row.status === "live" ? (
                        <button
                          type="button"
                          className="master-btn master-btn--delete"
                          disabled={busyId === row.id}
                          onClick={() => runAction(row.id, "end")}
                        >
                          End
                        </button>
                      ) : null}
                      {canManage && row.status === "scheduled" ? (
                        <button
                          type="button"
                          className="master-btn master-btn--delete"
                          disabled={busyId === row.id}
                          onClick={() => runAction(row.id, "cancel")}
                        >
                          Cancel
                        </button>
                      ) : null}
                      {canManage ? (
                        <button
                          type="button"
                          className="master-btn master-btn--edit"
                          onClick={() =>
                            navigate(
                              buildAppPath(
                                `online-classes/${row.id}/attendance`
                              )
                            )
                          }
                        >
                          Attendance
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
